import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import useStream from 'src/hooks/useStream';
import { useStreamStart } from 'src/hooks/useStreamStart';
import { useStreamEndNotification } from 'src/hooks/useStreamEndNotification';
import { StreamingApp } from 'src/state/Apps';
import { SolutionOmniverseApp } from './Solutions/types/solutionOmniverseApp';

const StreamContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: '#000',
}));

const StreamContent = styled(Box)({
  flex: 1,
  position: 'relative',
  boxSizing: 'border-box',
});

const VideoElement = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  boxSizing: 'border-box',
});

const AudioElement = styled('audio')({});

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  zIndex: 1,
});

const ErrorBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 500,
  textAlign: 'center',
}));

const ControlsBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'flex-end',
}));

/**
 * AppStream component displays the streaming session
 * Uses useStream hook to establish WebRTC connection
 */
export default function AppStream() {
  const { appId = '', sessionId = '' } = useParams<{
    appId: string;
    sessionId: string;
  }>();

  const navigate = useNavigate();

  // State for app data
  const [appData, setAppData] = useState<SolutionOmniverseApp | null>(null);
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const [appError, setAppError] = useState<string | null>(null);

  // Fetch app data from API
  useEffect(() => {
    const fetchAppData = async () => {
      if (!appId) {
        console.log('⚠️ Missing appId for app fetch');
        setIsLoadingApp(false);
        return;
      }

      try {
        console.log('📡 Fetching app data...');
        console.log('Params:', { appId });

        // Import pepApiRequest dynamically
        const { pepApiRequest } = await import('src/services/pepApi');
        const { RequestMethod } = await import('src/services/types');

        const url = `/apps/${appId}`;

        console.log('API URL:', url);

        const response = await pepApiRequest({
          method: RequestMethod.Get,
          url,
          useSessionProxy: true,
          credentials: 'include',
        });

        console.log('✅ App data fetched:', response);
        setAppData(response as SolutionOmniverseApp);
        setAppError(null);
      } catch (error) {
        console.error('❌ Failed to fetch app data:', error);
        setAppError(
          error instanceof Error ? error.message : 'Failed to load application'
        );
      } finally {
        setIsLoadingApp(false);
      }
    };

    fetchAppData();
  }, [appId]);

  console.log('=== AppStream Debug Info ===');
  console.log('📋 Params from URL:', { appId, sessionId });
  console.log('📦 App State:');
  console.log('  - appData:', appData);
  console.log('  - isLoadingApp:', isLoadingApp);
  console.log('  - appError:', appError);

  // Convert to StreamingApp type for useStream hook
  const streamingApp: StreamingApp | null = appData
    ? {
        id: appData.id,
        title: appData.title,
        icon: appData.icon || '',
        productArea: appData.productArea || '',
        latestVersion: { id: '', name: '' },
        versions: [],
        mediaServer: appData.mediaServer,
        mediaPort: appData.mediaPort,
      }
    : null;

  console.log('streamingApp:', streamingApp);

  // Hook to connect to existing session - only call if app is loaded
  const stream = useStream({
    app: streamingApp!, // Now guaranteed to have a value (at minimum from fallback)
    sessionId,
    videoElementId: 'stream-video',
    audioElementId: 'stream-audio',
  });

  // Hook to create new session (for "Start New Session" button)
  const streamStart = useStreamStart({
    appId,
  });

  // Hook to handle session end notifications
  useStreamEndNotification({
    sessionId,
  });

  // Fullscreen state
  const [fullScreen, setFullScreen] = useState(false);
  const videoElementRef = useRef<HTMLVideoElement>(null);

  const handleBack = () => {
    navigate(`/app/${appId}/sessions`);
  };

  const handleTerminate = async () => {
    if (
      window.confirm(
        'Are you sure you want to terminate the streaming session?'
      )
    ) {
      try {
        await stream.terminate();
        navigate(`/app/${appId}/sessions`);
      } catch (error) {
        console.error('Failed to terminate session:', error);
      }
    }
  };

  const handleStartNewSession = async () => {
    try {
      // Terminate current session
      await stream.terminate();

      // Start a new session
      await streamStart.mutateAsync();

      // Reload to connect to new session
      window.location.reload();
    } catch (error) {
      console.error('Failed to start new session:', error);
    }
  };

  const toggleFullScreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }

      // Focus the video element after toggling fullscreen
      if (videoElementRef.current) {
        videoElementRef.current.click();
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
    }
  };

  // Sync fullscreen state
  useEffect(() => {
    const syncFullScreen = () => {
      setFullScreen(document.fullscreenElement != null);
    };
    document.addEventListener('fullscreenchange', syncFullScreen);
    return () =>
      document.removeEventListener('fullscreenchange', syncFullScreen);
  }, []);

  if (!streamingApp || isLoadingApp) {
    return (
      <StreamContainer>
        <LoadingOverlay>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
            {appError ? 'Failed to load application' : 'Loading application...'}
          </Typography>
          {appError && (
            <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
              {appError}
            </Typography>
          )}
        </LoadingOverlay>
      </StreamContainer>
    );
  }

  return (
    <StreamContainer>
      {!fullScreen && (
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {streamingApp.title} - Streaming Session
            </Typography>
            <IconButton
              color="inherit"
              onClick={toggleFullScreen}
              sx={{ mr: 1 }}
            >
              {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <Button color="inherit" onClick={handleStartNewSession}>
              Start New Session
            </Button>
            <Button color="inherit" onClick={handleTerminate}>
              End Session
            </Button>
          </Toolbar>
        </AppBar>
      )}

      <StreamContent>
        {/* Video element for stream display */}
        <VideoElement
          id="stream-video"
          ref={videoElementRef}
          autoPlay
          playsInline
        />

        {/* Audio element for stream audio */}
        <AudioElement id="stream-audio" autoPlay muted />

        {/* Loading overlay */}
        {stream.loading && (
          <LoadingOverlay>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
              Connecting to streaming session...
            </Typography>
          </LoadingOverlay>
        )}

        {/* Error overlay */}
        {(stream.error || streamStart.error) && (
          <LoadingOverlay>
            <ErrorBox>
              <Typography variant="h5" color="error" gutterBottom>
                Connection Error
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', mb: 3 }}>
                {typeof stream.error === 'string'
                  ? stream.error
                  : stream.error?.message || streamStart.error?.toString()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => window.location.reload()}
                  disabled={streamStart.isPending}
                >
                  Reload
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartNewSession}
                  disabled={streamStart.isPending}
                >
                  {streamStart.isPending ? 'Starting...' : 'Start New Session'}
                </Button>
              </Box>
            </ErrorBox>
          </LoadingOverlay>
        )}
      </StreamContent>

      {!fullScreen && (
        <ControlsBar>
          <Typography variant="body2" color="text.secondary">
            Session ID: {sessionId}
          </Typography>
        </ControlsBar>
      )}
    </StreamContainer>
  );
}
